"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import {
  BarChart3, BookOpen, Braces, ChevronDown, Grid3x3, Menu, Plug, Search, ShieldCheck, Sparkles, X,
} from "lucide-react";
import { Logo } from "@/components/ui/logo";
import { Button } from "@/components/ui/button";
import { MODULE_GROUPS, NAV_LINKS, SALES_URL, SIGNIN_URL, SIGNUP_URL, type ModuleLink } from "@/lib/links";
import { cn } from "@/lib/utils";

const ICONS: Record<ModuleLink["icon"], React.ComponentType<{ className?: string }>> = {
  Plug, Grid3x3, Braces, BarChart3, ShieldCheck, BookOpen, Search, Sparkles,
};

export function Nav() {
  const pathname = usePathname();
  const [productOpen, setProductOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setProductOpen(false);
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (productOpen) triggerRef.current?.focus();
      setProductOpen(false);
      setMobileOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setProductOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [productOpen]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const productActive = pathname.startsWith("/features") || pathname === "/meet-juno";

  return (
    <header ref={wrapRef} className="fixed inset-x-0 top-0 z-[90] border-b border-line bg-white">
      <div className="mx-auto flex h-16 max-w-container items-center gap-8 px-6 md:px-10">
        <Link href="/" aria-label="Stirrup home" className="shrink-0 rounded-btn">
          <Logo />
        </Link>

        <nav aria-label="Primary" className="hidden h-full items-center gap-1 lg:flex">
          <button
            ref={triggerRef}
            type="button"
            aria-expanded={productOpen}
            aria-controls="product-menu"
            onClick={() => setProductOpen((v) => !v)}
            className={cn(
              "flex h-full items-center gap-1 border-b-2 px-3 text-[0.9375rem] font-medium transition-colors",
              productOpen || productActive ? "border-navy text-navy" : "border-transparent text-slate hover:text-navy",
            )}
          >
            Product
            <ChevronDown aria-hidden className={cn("h-4 w-4 transition-transform", productOpen && "rotate-180")} />
          </button>
          {NAV_LINKS.map((l) => {
            const active = pathname === l.href;
            return (
              <Link
                key={l.href}
                href={l.href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex h-full items-center border-b-2 px-3 text-[0.9375rem] font-medium transition-colors",
                  active ? "border-navy text-navy" : "border-transparent text-slate hover:text-navy",
                )}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto hidden items-center gap-2 lg:flex">
          <Link href={SIGNIN_URL} className="px-3 text-[0.9375rem] font-medium text-slate hover:text-navy">
            Sign in
          </Link>
          <Button href={SALES_URL} variant="secondary" size="sm">
            Contact sales
          </Button>
          <Button href={SIGNUP_URL} size="sm">
            Start free
          </Button>
        </div>

        <button
          type="button"
          className="ml-auto rounded-btn p-2 text-navy lg:hidden"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* desktop product menu */}
      {productOpen && (
      <div
        id="product-menu"
        className="absolute inset-x-0 top-full hidden border-b border-line bg-white shadow-menu lg:block"
      >
        <div className="mx-auto grid max-w-container grid-cols-[1fr_1fr_18rem] gap-10 px-10 py-8">
          {MODULE_GROUPS.map((g) => (
            <div key={g.name}>
              <h2 className="mb-3 text-small font-semibold text-slate">{g.name}</h2>
              <ul className="grid gap-1">
                {g.items.map((m) => {
                  const Icon = ICONS[m.icon];
                  return (
                    <li key={m.href}>
                      <Link href={m.href} className="group flex gap-3 rounded-panel p-3 hover:bg-fog-2">
                        <Icon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-slate group-hover:text-navy" />
                        <span>
                          <span className="block font-semibold text-navy">{m.label}</span>
                          <span className="block text-small text-slate">{m.desc}</span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          <div className="rounded-panel bg-fog p-6">
            <h2 className="font-semibold text-navy">How it fits together</h2>
            <p className="mt-2 text-small text-slate">
              Every module reads the same connections and writes to the same audit log. See the architecture.
            </p>
            <div className="mt-5 grid gap-2">
              <Link href="/platform" className="prose-link text-small">
                Platform overview
              </Link>
              <Link href="/features" className="prose-link text-small">
                All features
              </Link>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* mobile drawer */}
      <div
        id="mobile-menu"
        hidden={!mobileOpen}
        className="h-[calc(100dvh-4rem)] overflow-y-auto border-t border-line bg-white px-6 pb-10 pt-6 lg:hidden"
      >
        {MODULE_GROUPS.map((g) => (
          <div key={g.name} className="mb-6">
            <h2 className="mb-2 text-small font-semibold text-slate">{g.name}</h2>
            <ul>
              {g.items.map((m) => (
                <li key={m.href}>
                  <Link href={m.href} className="block py-2 text-[1.0625rem] font-medium text-navy">
                    {m.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
        <ul className="border-t border-line pt-4">
          {[{ href: "/features", label: "All features" }, ...NAV_LINKS].map((l) => (
            <li key={l.href}>
              <Link href={l.href} className="block py-2 text-[1.0625rem] font-medium text-navy">
                {l.label}
              </Link>
            </li>
          ))}
          <li>
            <Link href={SIGNIN_URL} className="block py-2 text-[1.0625rem] font-medium text-navy">
              Sign in
            </Link>
          </li>
        </ul>
        <div className="mt-6 grid gap-3">
          <Button href={SIGNUP_URL}>Start free</Button>
          <Button href={SALES_URL} variant="secondary">
            Contact sales
          </Button>
        </div>
      </div>
    </header>
  );
}
